import { useState } from "react";
import { Plus, Pencil, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtL } from "@/lib/api";
import { PageHeader, Badge, useConfirm } from "@/components/opl/Common";
import { useList, FormDialog, DataTable, GoldBtn, IconBtn } from "@/components/opl/Crud";
import { Switch } from "@/components/ui/switch";

const FIELDS = [
  { name: "name", label: "Category name", required: true }, { name: "base_price", label: "Base price (points)", type: "number", required: true },
  { name: "color", label: "Colour", type: "color" }, { name: "enabled", label: "Enabled", type: "switch" }, { name: "description", label: "Description", type: "textarea", full: true },
];

export default function Categories() {
  const [rows, load] = useList("/categories");
  const [edit, setEdit] = useState(null);
  const [dialog, confirm] = useConfirm();
  const save = async (v) => {
    if (v.id) await api.put(`/categories/${v.id}`, v); else await api.post("/categories", v);
    toast.success("Category saved");
    load();
  };
  const move = async (i, d) => {
    const ids = rows.map((r) => r.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    await api.put("/categories/reorder", ids);
    load();
  };
  return (
    <div>
      {dialog}
      <PageHeader title="Player Categories" crumbs={["Categories"]}><GoldBtn testid="add-category-btn" onClick={() => setEdit({ enabled: true, color: "#E5B842" })}><Plus className="h-4 w-4" />Add category</GoldBtn></PageHeader>
      <p className="text-sm text-slate-400 mb-4">Order here drives the nomination order when it is set to "category". Base prices apply unless a player has an override.</p>
      <DataTable rows={rows} testid="categories-table" columns={[
        { key: "order", label: "#", render: (r) => <span className="num text-slate-500">{r.order}</span> },
        { key: "name", label: "Category", render: (r) => <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: r.color }} /><b>{r.name}</b></span> },
        { key: "base_price", label: "Base price", render: (r) => <span className="num">{fmtL(r.base_price)}</span> },
        { key: "enabled", label: "Enabled", render: (r) => <Switch checked={r.enabled} data-testid={`toggle-category-${r.name}`} onCheckedChange={async (c) => { try { await api.put(`/categories/${r.id}`, { ...r, enabled: c }); load(); } catch (e) { toast.error(errMsg(e)); } }} /> },
        { key: "st", label: "", render: (r) => !r.enabled && <Badge className="text-slate-400">Disabled</Badge> },
        { key: "a", label: "", render: (r) => { const i = rows.indexOf(r); return <div className="flex justify-end">
          <IconBtn testid={`up-category-${r.name}`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></IconBtn>
          <IconBtn testid={`down-category-${r.name}`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></IconBtn>
          <IconBtn testid={`edit-category-${r.name}`} onClick={() => setEdit(r)}><Pencil className="h-4 w-4" /></IconBtn>
          <IconBtn testid={`delete-category-${r.name}`} onClick={() => confirm({ title: `Delete ${r.name}?`, description: "Categories in use cannot be deleted; disable them instead.", destructive: true, confirmLabel: "Delete", onConfirm: async () => { try { await api.delete(`/categories/${r.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } })}><Trash2 className="h-4 w-4" /></IconBtn>
        </div>; } },
      ]} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit category" : "Add category"} fields={FIELDS} initial={edit} onSubmit={save} />
    </div>
  );
}
